import { createContext, useState, useMemo, useEffect, useContext } from 'react';
import { api } from '../../../services/api';
import { useMisc } from '../../../contexts/MiscContext';
import { toast } from 'react-toastify';

export const ChamadosSourcingContext = createContext();
export const useChamadosSourcingContext = () => useContext(ChamadosSourcingContext);

export function ChamadosSourcingProvider({ children }) {
  const { setShowLoading } = useMisc();
  const [chamados, setChamados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMainTable, setLoadingMainTable] = useState(false);

  const [showModalNovoChamado, setShowModalNovoChamado] = useState(false);
  // 101 - Novo fornecedor | 102 - Cadeia de fornecimento
  const [tipoFormulario, setTipoFormulario] = useState(null);
  const [showDetalhes, setShowDetalhes] = useState(false);
  const [formularioFiltro, setFormularioFiltro] = useState({
    id_chamado: '',
    lacre_envio: '',
    cnpj: '',
    categoria: null,
    status_cq: null,
    resultado: null,
    data_inicio: '',
    data_fim: '',
  });
  const [ultimosFiltrosUsados, setUltimosFiltrosUsados] = useState({
    id_chamado: '',
    lacre_envio: '',
    cnpj: '',
    categoria: null,
    status_cq: null,
    resultado: null,
    data_inicio: '',
    data_fim: '',
  });
  const [dadosChamadoAtivo, setDadosChamadoAtivo] = useState({});
  const [listaDefeitosMedidas, setListaDefeitosMedidas] = useState([]);
  const [listaDefeitosAcabamento, setListaDefeitosAcabamento] = useState([]);
  const [listaDefeitosCostura, setListaDefeitosCostura] = useState([]);

  const inicializar = async () => {
    try {
      setLoading(true);
      const res = await api.get('pa/chamados-sourcing/inicializacao');
      setListaDefeitosMedidas(res.data.listaDefeitosMedidas);
      setListaDefeitosAcabamento(res.data.listaDefeitosAcabamento);
      setListaDefeitosCostura(res.data.listaDefeitosCostura);
    } catch (e) {
      toast.error('Ocorreu um erro durante a inicialização. Por favor faça um refresh na página ou entre em contato com o suporte.');
    } finally {
      setLoading(false);
    }
  }

  const carregarChamados = async (update = false) => {
    try {
      setLoading(true);
      setLoadingMainTable(true);
      const res = await api.post('pa/chamados-sourcing/get-chamados', update ? ultimosFiltrosUsados : formularioFiltro);
      setChamados([...res.data]);
      if (!update) setUltimosFiltrosUsados({ ...formularioFiltro });
    } catch (e) {
      toast.error('Ocorreu um erro ao carregar os chamados. Por favor tente novamente ou entre em contato com o suporte.');
    } finally {      
      setLoading(false);
      setLoadingMainTable(false);
    }
  }

  useEffect(() => {
    inicializar();
    carregarChamados();
  }, []);

  const abrirDetalhesChamado = async (chamado) => {
    try {
      setShowLoading(true);
      const res = await api.get('pa/chamados-sourcing/get-dados-chamado', {
        params: { id_chamado: chamado.id_chamado, produto: chamado.referencia_produto }
      });
      setDadosChamadoAtivo({ ...chamado, ...res.data });
      setShowDetalhes(true);
    } catch (e) {
      toast.error('Ocorreu um erro ao carregar os dados do chamado. Por favor tente novamente ou entre em contato com o suporte.');
    } finally {
      setShowLoading(false);
    }
  }

  const fecharModalDetalhes = () => {
    setDadosChamadoAtivo({});
    setShowDetalhes(false);
  }

  const limparFiltros = () => {
    setFormularioFiltro({
      id_chamado: '',
      lacre_envio: '',
      cnpj: '',
      categoria: null,
      status_cq: null,
      resultado: null,
      data_inicio: '',
      data_fim: ''
    });
  }

  const contextValue = useMemo(() => ({
    formularioFiltro, setFormularioFiltro,
    ultimosFiltrosUsados, setUltimosFiltrosUsados,
    tipoFormulario, setTipoFormulario,
    chamados,
    loading,
    showModalNovoChamado,
    setShowModalNovoChamado,
    carregarChamados,
    listaDefeitosMedidas,
    listaDefeitosAcabamento,
    listaDefeitosCostura,
    abrirDetalhesChamado,
    dadosChamadoAtivo,
    showDetalhes,
    fecharModalDetalhes,
    loadingMainTable,
    limparFiltros
  }), [
    formularioFiltro, setFormularioFiltro,
    ultimosFiltrosUsados, setUltimosFiltrosUsados,
    tipoFormulario, setTipoFormulario,
    chamados,
    loading,
    showModalNovoChamado,
    setShowModalNovoChamado,
    carregarChamados,
    listaDefeitosMedidas,
    listaDefeitosAcabamento,
    listaDefeitosCostura,
    abrirDetalhesChamado,
    dadosChamadoAtivo,
    showDetalhes,
    fecharModalDetalhes,
    loadingMainTable,
    limparFiltros
  ]);

  return (
    <ChamadosSourcingContext.Provider value={contextValue}>
      {children}
    </ChamadosSourcingContext.Provider>
  );
}
